import { get, isEmpty, isEqual, isNil, pick } from "lodash";
import { draftCopyOf } from "utils/helpers/screenDraft";
import {
  checkKindOf,
  checkOf,
  checkVariableOf,
  CHECK_KINDS,
  isMustBeChecked,
  shownRules,
} from "./answer-checks";
import {
  BINDING_KINDS,
  fieldPathOf,
  fillTemplate,
  isFieldToCreate,
  isLinked,
  isPictureToProvide,
  SLOT_TYPES,
  slotKindOf,
  slotSummary,
} from "./workshop-view";

export const FIELD_FILTERS = { ALL: "all", FIX: "fix" };

export const FIELD_GROUPS = { TOP: "group:top", ELSEWHERE: "group:elsewhere" };

export const SLOT_PROBLEMS = {
  TO_CREATE: "TO_CREATE",
  PICTURE: "PICTURE",
  UNLINKED: "UNLINKED",
};

export const PILL_TONES = { FIX: "fix", DRAFT: "draft", KIND: "kind" };

const HEADINGS = "h1,h2,h3,h4,h5,h6,[role='heading']";
const SLOT_ATTRIBUTE = "data-kayros-slot";
const HEADING_SIZE = 60;
const SLOT_KEYS = [
  "type",
  "collection",
  "binding",
  "src",
  "placeholder",
  "localizableAttributes",
];

const plainText = (value, size) => {
  const text = String(value || "")
    .replace(/\s+/g, " ")
    .trim();
  return text.length > size ? `${text.slice(0, size)}…` : text;
};

export const slotHeadings = (html) => {
  const found = {};
  if (isEmpty(html) || typeof DOMParser === "undefined") return found;
  const doc = new DOMParser().parseFromString(html, "text/html");
  let heading = null;
  let position = 0;
  for (const element of doc.querySelectorAll(
    `${HEADINGS},[${SLOT_ATTRIBUTE}]`,
  )) {
    const text = plainText(element.textContent, HEADING_SIZE);
    if (element.matches(HEADINGS) && text) heading = text;
    const id = element.getAttribute(SLOT_ATTRIBUTE);
    if (!isNil(id) && isNil(found[id])) {
      found[id] = { heading, position };
      position += 1;
    }
  }
  return found;
};

export const slotProblem = (slot, known) => {
  if (isFieldToCreate(slot, known)) return SLOT_PROBLEMS.TO_CREATE;
  if (isPictureToProvide(slot)) return SLOT_PROBLEMS.PICTURE;
  if (!isLinked(slot)) return SLOT_PROBLEMS.UNLINKED;
  return null;
};

const signatureOf = (slot) =>
  JSON.stringify([
    slot.id,
    ...SLOT_KEYS.map((key) => (isNil(slot[key]) ? null : slot[key])),
  ]);

const sameSlot = (current, before) =>
  !isNil(before) && isEqual(pick(current, SLOT_KEYS), pick(before, SLOT_KEYS));

const publishedSlotsOf = (published, state) => {
  if (isNil(state)) return get(published, "slots") || [];
  const copy = draftCopyOf(get(published, "states") || [], state);
  return get(copy, "bindingManifest.slots") || [];
};

const changedSince = (published, place, slot) =>
  !isNil(published) &&
  !sameSlot(
    slot,
    publishedSlotsOf(published, place.state).find(
      (item) => item.id === slot.id,
    ),
  );

const checkChanged = ({ formDefinition, liveFormDefinition, variable }) =>
  !isNil(variable) &&
  liveFormDefinition !== undefined &&
  !isEqual(
    checkOf(formDefinition, variable),
    checkOf(liveFormDefinition, variable),
  );

const placedSlots = (target) => {
  const headings = slotHeadings(target.html);
  return (target.slots || [])
    .map((slot, index) => ({
      slot,
      index,
      spot: headings[slot.id] || null,
    }))
    .sort((a, b) => {
      const left = isNil(a.spot) ? Infinity : a.spot.position;
      const right = isNil(b.spot) ? Infinity : b.spot.position;
      return left === right ? a.index - b.index : left - right;
    });
};

export const fieldRows = ({
  targets = [],
  known = null,
  variables = [],
  formDefinition = null,
  liveFormDefinition,
  published = null,
}) => {
  const rows = [];
  const bySignature = {};
  for (const target of targets) {
    for (const { slot, spot } of placedSlots(target)) {
      const place = {
        key: target.key,
        stateId: target.stateId,
        state: target.state,
        name: target.name,
        slotId: slot.id,
      };
      const signature = signatureOf(slot);
      const existing = bySignature[signature];
      if (existing) {
        existing.places.push(place);
        existing.draft = existing.draft || changedSince(published, place, slot);
        continue;
      }
      const variable = checkVariableOf(slot, variables);
      const row = {
        key: `${target.placeKey || target.key}:${slot.id}`,
        slot,
        places: [place],
        variable,
        group: isNil(spot)
          ? FIELD_GROUPS.ELSEWHERE
          : spot.heading || FIELD_GROUPS.TOP,
        problem: slotProblem(slot, known),
        draft:
          changedSince(published, place, slot) ||
          checkChanged({ formDefinition, liveFormDefinition, variable }),
      };
      bySignature[signature] = row;
      rows.push(row);
    }
  }
  return rows;
};

export const fieldGroups = (rows = []) => {
  const groups = [];
  const byKey = {};
  for (const row of rows) {
    if (isNil(byKey[row.group])) {
      byKey[row.group] = { key: row.group, rows: [] };
      groups.push(byKey[row.group]);
    }
    byKey[row.group].rows.push(row);
  }
  return [
    ...groups.filter((group) => group.key !== FIELD_GROUPS.ELSEWHERE),
    ...groups.filter((group) => group.key === FIELD_GROUPS.ELSEWHERE),
  ];
};

export const groupTitle = (key, labels) => {
  if (key === FIELD_GROUPS.TOP) return labels.TOP;
  if (key === FIELD_GROUPS.ELSEWHERE) return labels.ELSEWHERE;
  return key;
};

const withLabel = (path, known) => (known || {})[path] || path;

const shownSource = (slot, known, labels) => {
  const binding = get(slot, "binding") || {};
  if (binding.kind === BINDING_KINDS.DATA)
    return withLabel(binding.source, known);
  if (binding.kind === BINDING_KINDS.ENTITY_FIELD)
    return withLabel(fieldPathOf(slot), known);
  if (slot.type === SLOT_TYPES.IMAGE)
    return slot.placeholder ? labels.EMPTY_PICTURE : labels.FIXED_PICTURE;
  return fillTemplate(labels.FIXED_SOURCE, {
    text: plainText(binding.sourceValue || slot.label, 40),
  });
};

export const requiredText = (check, kind, labels) => {
  if (kind === CHECK_KINDS.CHECKBOX)
    return isMustBeChecked(check) ? labels.MUST_BE_CHECKED : labels.OPTIONAL;
  return get(check, "required") ? labels.REQUIRED : labels.OPTIONAL;
};

export const ruleTitle = (rule, ruleLabels) =>
  get(ruleLabels, [rule.validator, "TITLE"]) || rule.validator;

export const checkText = (check, labels, ruleLabels) => {
  const [first] = shownRules(check);
  return isNil(first) ? labels.NO_CHECK : ruleTitle(first, ruleLabels);
};

export const rowSummary = (
  row,
  { labels, fieldLabels, ruleLabels, known, formDefinition },
) => {
  const { slot, variable, problem } = row;
  if (problem === SLOT_PROBLEMS.TO_CREATE)
    return fillTemplate(fieldLabels.TO_CREATE_SUB, {
      field: fieldPathOf(slot),
    });
  if (problem === SLOT_PROBLEMS.PICTURE) return fieldLabels.TO_PROVIDE_SUB;
  if (problem === SLOT_PROBLEMS.UNLINKED) return fieldLabels.UNLINKED;
  if (slot.type === SLOT_TYPES.ACTION)
    return slotSummary(slot, fieldLabels, known || {});
  if (slot.type !== SLOT_TYPES.INPUT)
    return fillTemplate(labels.SHOWS, {
      source: shownSource(slot, known, labels),
    });
  if (isNil(variable))
    return fillTemplate(labels.SAVED_IN, {
      field: withLabel(fieldPathOf(slot) || get(slot, "binding.source"), known),
    });
  const check = checkOf(formDefinition, variable);
  return fillTemplate(labels.SUMMARY, {
    required: requiredText(check, checkKindOf(slot, variable), labels),
    check: checkText(check, labels, ruleLabels),
  });
};

export const rowPill = (row, labels) => {
  if (!isNil(row.problem)) return { tone: PILL_TONES.FIX, text: labels.FIX };
  if (row.draft) return { tone: PILL_TONES.DRAFT, text: labels.DRAFT };
  return {
    tone: PILL_TONES.KIND,
    text:
      get(labels.KINDS, slotKindOf(row.slot)) ||
      get(labels.KINDS, row.slot.type) ||
      row.slot.type,
  };
};

export const technicalPath = (slot) => {
  const binding = get(slot, "binding") || {};
  if (binding.kind === BINDING_KINDS.ENTITY_FIELD) return fieldPathOf(slot);
  if (binding.kind === BINDING_KINDS.DATA) return binding.source || "";
  if (binding.kind === BINDING_KINDS.SET_FIELD)
    return `${binding.entity}.${binding.field} = ${binding.value ?? ""}`;
  if (!isNil(binding.name)) return binding.name;
  return binding.kind || "";
};
